import { ReactNode, useCallback, useMemo } from 'react';
import {
  Address,
  onlyPublicLabelsFilter,
  useEntityAssignments,
  useGroupRules,
  UserGroupRules,
} from 'src/graphql';
import { SITE_CARD_PROPERTY_KEYS } from 'src/graphql/entities/side-panel-personalization/model/card-keys';
import { ConditionInput } from 'src/graphql/types';
import {
  AssociatedSiteFormValues,
  useAssociatedSiteForm,
} from 'src/pages/Conversations/SidePanel/ConversationDetails/ConversationSite/use-associated-site-form';
import { useUpdateAssociatedSite } from 'src/pages/Sites/use-update-associated-site';
import { SiteCardFormContext, SiteCardFormData } from './site-card-form-context';

/**
 * Owns the associated-site form state for the Site page so the base fields can
 * render as individually ordered panel rows (site-card-rows.tsx) instead of one
 * card-rendered form. Must mount ABOVE SidePanelPersonalizationProvider: the
 * surface hook reads `isEmptyByKey` for hide-empty, and being outside the
 * personalization context keeps useAssociatedSiteForm's own visibility gating
 * inert (the engine applies visibility to the rows instead).
 */
export const SiteCardFormProvider = ({
  address,
  disabled,
  children,
}: {
  address: Address;
  disabled: boolean;
  children: ReactNode;
}) => {
  const { associatedSiteFormFields } = useAssociatedSiteForm(address, disabled);
  const updateAssociatedSite = useUpdateAssociatedSite(address);

  const onSubmit = useCallback(
    (values: AssociatedSiteFormValues) => {
      void updateAssociatedSite(values);
    },
    [updateAssociatedSite],
  );

  const { groupRules } = useGroupRules();
  const canAccessPrivateLabels = Boolean(groupRules[UserGroupRules.AccessToPrivateLabels]);

  const labelsFilters: ConditionInput[] = useMemo(
    () => (canAccessPrivateLabels ? [] : [onlyPublicLabelsFilter]),
    [canAccessPrivateLabels],
  );

  const { labelAssignments } = useEntityAssignments({
    entity: 'Address',
    ids: address.addressId,
    filters: labelsFilters,
    skip: !groupRules,
  });

  const value = useMemo<SiteCardFormData>(() => {
    const serviceLevel = associatedSiteFormFields.find(
      (field) => field.name === 'serviceLevel',
    );
    const accountManager = associatedSiteFormFields.find(
      (field) => field.name === 'accountManager',
    );

    return {
      address,
      disabled,
      fieldsByKey: {
        [SITE_CARD_PROPERTY_KEYS.serviceLevel]: serviceLevel,
        [SITE_CARD_PROPERTY_KEYS.accountManager]: accountManager,
      },
      onSubmit,
      labelAssignments,
      canAccessPrivateLabels,
      isEmptyByKey: {
        [SITE_CARD_PROPERTY_KEYS.serviceLevel]: !serviceLevel?.defaultValue,
        // the field's `defaultValue` carries the current owner even mid-search,
        // so hide-empty doesn't flicker while the user types
        [SITE_CARD_PROPERTY_KEYS.accountManager]: !accountManager?.defaultValue,
        [SITE_CARD_PROPERTY_KEYS.labels]: !labelAssignments?.length,
      },
    };
  }, [
    address,
    disabled,
    associatedSiteFormFields,
    onSubmit,
    labelAssignments,
    canAccessPrivateLabels,
  ]);

  return <SiteCardFormContext.Provider value={value}>{children}</SiteCardFormContext.Provider>;
};
